import { useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'react-toastify';

import {
  useListAddresses,
  useListCustomers,
  useEnableCustomerLocationLoadOption,
  useDisableCustomerLocationLoadOption,
  useSetCustomerLocationProductAccess,
} from '../../features/customers/api';

import { useCreateCustomerOrder } from '../../features/customer-orders/api';

import { useListProducts } from '../../features/products/api';

import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';

import { useListLoadOptions } from '../../features/loads/api';

import { useListLocations } from '../../features/warehouses/api';

import { limitMoneyInput } from '../../utils/formatters';

import SearchInput from '../SearchInput/SearchInput';
import { ProductAccessManager } from '../ProductAccessManager/ProductAccessManager';
import { LocationLoadOptionManager } from '../LocationLoadOptionManager/LocationLoadOptionManager';

import { SearchableSelect } from '../SearchableSelect/SearchableSelect';

import {
  activeLocationLoadOptions,
  addressDefaultLoadOptionId,
  addressLabel,
  buildAdminOrderPayload,
  buildLoadOptions,
  customerLabel as formatCustomerLabel,
  effectiveDefaultLoadOptionId,
  findOrganizationDefault,
} from '../../features/customer-orders/adminOrderForm';

import './CustomerOrderDrawer.scss';

import './CustomerOrderDrawer.multi.scss';

function centsToDollarsInput(value: unknown): string {
  const cents = Number(value ?? 0);

  if (!Number.isFinite(cents)) {
    return '0.00';
  }

  return (cents / 100).toFixed(2);
}

function dollarsInputToCents(value: string): number {
  return Math.round(Number(value) * 100);
}

type ProductLocationAccessState = 'ENABLED' | 'PREVIEW' | 'DISABLED';

type LocationAwareProduct = {
  id: string;

  name: string;

  description?: string | null;

  imageUrl?: string | null;

  deliveryType?: string | null;

  defaultLocationId?: string | null;

  stocks?: Array<{
    locationId?: string | null;
  }>;

  palletSizes?: any[];
};

type BatchRowState = {
  key: string;

  productId: string;

  quantity: string;

  poNumber: string;

  deliveryAddressId: string;

  warehouseId: string;

  loadOptionId: string;

  palletSizeId: string;

  carrierRateInput: string;

  carrierRateTouched: boolean;

  note: string;

  error: string | null;
};

const MAX_ORDERS = 50;

let rowKeySequence = 0;

const nextRowKey = () => `admin-order-${++rowKeySequence}`;

function productAccessAtAddress(
  address: any,

  productId: string,

  perItemPricing: boolean
): ProductLocationAccessState {
  if (!productId) {
    return 'DISABLED';
  }

  if (!perItemPricing) {
    return 'ENABLED';
  }

  const assignments = Array.isArray(address?.products) ? address.products : [];

  const assignment = assignments.find((row: any) => row?.productId === productId);

  if (assignment?.isEnabled === true) {
    return 'ENABLED';
  }

  if (assignment?.isPreview === true) {
    return 'PREVIEW';
  }

  return 'DISABLED';
}

type ManagedLocationProduct = LocationAwareProduct & {
  access: ProductLocationAccessState;
};

function splitProductsByLocationAccess(products: ManagedLocationProduct[]) {
  return {
    enabled: products.filter((product) => product.access === 'ENABLED'),
    preview: products.filter((product) => product.access === 'PREVIEW'),
    disabled: products.filter((product) => product.access === 'DISABLED'),
  };
}

function productLocationLabel(address: any): string {
  if (!address) {
    return '';
  }

  return [
    address.label || address.fullName,
    address.line1,
    address.city,
    address.state,
    address.postalCode,
  ]
    .filter(Boolean)
    .join(', ');
}

function apiErrorMessage(error: any, fallback: string): string {
  return (
    error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback
  );
}

function makeEmptyRow(): BatchRowState {
  return {
    key: nextRowKey(),

    productId: '',

    quantity: '',

    poNumber: '',

    deliveryAddressId: '',

    warehouseId: '',

    loadOptionId: '',

    palletSizeId: '',

    carrierRateInput: '',

    carrierRateTouched: false,

    note: '',

    error: null,
  };
}

export function CustomerOrderDrawer({
  open,

  customerId,

  customerLabel,

  onClose,
}: {
  open: boolean;

  customerId?: string;

  customerLabel?: string;

  onClose: () => void;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [customerSearch, setCustomerSearch] = useState('');

  const debouncedCustomerSearch = useDebouncedSearch(customerSearch, 350);

  const [selectedCustomerId, setSelectedCustomerId] = useState(customerId ?? '');

  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

  const [showCustomerList, setShowCustomerList] = useState(false);

  const customerComboRef = useRef<HTMLDivElement>(null);

  const resolvedCustomerId = customerId ?? selectedCustomerId;

  const customersQuery = useListCustomers({
    page: 1,

    pageSize: 20,

    search: debouncedCustomerSearch,
  });

  const customers = useMemo(
    () => (Array.isArray(customersQuery.data?.customers) ? customersQuery.data.customers : []),

    [customersQuery.data]
  );

  const derivedSelectedCustomer = useMemo(
    () => customers.find((customer: any) => customer.id === resolvedCustomerId) ?? null,

    [customers, resolvedCustomerId]
  );

  const effectiveSelectedCustomer = selectedCustomer ?? derivedSelectedCustomer;

  const {
    data: addressesProp,
    isLoading: addressesLoading,
    refetch: refetchAddresses,
  } = useListAddresses(resolvedCustomerId || null);

  const addresses = useMemo(
    () => (Array.isArray(addressesProp?.addresses) ? addressesProp.addresses : []),

    [addressesProp]
  );

  const perItemPricing = addressesProp?.perItemPricing === true;

  const productsQuery = useListProducts({
    sortingKey: 'updatedAt',

    sortingOrder: 'desc',

    searchQuery: '',

    page: 1,

    pageSize: 1000,

    loadStatus: 'all',

    problemFirst: false,

    archived: 'exclude',
  });

  const organizationProducts = useMemo(
    () => (Array.isArray(productsQuery.data?.products) ? productsQuery.data.products : []),

    [productsQuery.data]
  );

  const products = useMemo<LocationAwareProduct[]>(
    () =>
      organizationProducts.map((product: any) => ({
        ...product,

        id: String(product.id),

        name: String(product.name ?? ''),
      })),

    [organizationProducts]
  );

  const { data: allLocationsData, isLoading: locationsLoading } = useListLocations();

  const allLocations = useMemo(
    () => (Array.isArray(allLocationsData) ? allLocationsData : []),

    [allLocationsData]
  );

  const { data: organizationLoadOptions = [], isLoading: loadOptionsLoading } =
    useListLoadOptions();

  const organizationDefaultLoadOption = useMemo(
    () => findOrganizationDefault(organizationLoadOptions),

    [organizationLoadOptions]
  );

  /*

   * Product picker = union of products enabled at any customer delivery address.

   * When per-item pricing is off, every active organization product is available.

   */

  const enabledAddressIdsByProductId = useMemo(() => {
    const result = new Map<string, Set<string>>();

    if (!perItemPricing) {
      return result;
    }

    for (const address of addresses) {
      const assignments = Array.isArray(address?.products) ? address.products : [];

      for (const assignment of assignments) {
        if (!assignment?.productId || assignment.isEnabled !== true) {
          continue;
        }

        let ids = result.get(assignment.productId);

        if (!ids) {
          ids = new Set<string>();

          result.set(assignment.productId, ids);
        }

        ids.add(address.id);
      }
    }

    return result;
  }, [addresses, perItemPricing]);

  const orderableProducts = useMemo(
    () =>
      products.filter((product) => {
        if (!perItemPricing) {
          return true;
        }

        return (enabledAddressIdsByProductId.get(product.id)?.size ?? 0) > 0;
      }),

    [products, perItemPricing, enabledAddressIdsByProductId]
  );

  const productOptions = useMemo(
    () => orderableProducts.map((product) => ({ id: product.id, name: product.name })),

    [orderableProducts]
  );

  const [rows, setRows] = useState<BatchRowState[]>([makeEmptyRow()]);

  /*
   * Management panels are scoped to one order row because both product access
   * and fulfilment methods belong to a specific customer delivery location.
   */
  const [manager, setManager] = useState<{
    kind: 'products' | 'methods';
    rowKey: string;
  } | null>(null);

  const [managerSearch, setManagerSearch] = useState('');
  const debouncedManagerSearch = useDebouncedSearch(managerSearch, 350);
  const [enablingProduct, setEnablingProduct] = useState(false);

  const order = useCreateCustomerOrder();

  const setLocationProductAccess = useSetCustomerLocationProductAccess();

  const enableLocationLoadOption = useEnableCustomerLocationLoadOption(resolvedCustomerId || null);

  const disableLocationLoadOption = useDisableCustomerLocationLoadOption(
    resolvedCustomerId || null
  );

  const methodChangePending =
    enableLocationLoadOption.isPending || disableLocationLoadOption.isPending;

  const productForRow = (row: BatchRowState) =>
    products.find((product) => product.id === row.productId) ?? null;

  const addressForRow = (row: BatchRowState) =>
    addresses.find((address: any) => address.id === row.deliveryAddressId) ?? null;

  const managerRow = manager ? (rows.find((row) => row.key === manager.rowKey) ?? null) : null;

  const managerAddress = managerRow ? addressForRow(managerRow) : null;

  const managerProducts = useMemo<ManagedLocationProduct[]>(() => {
    if (!managerAddress) {
      return [];
    }

    const query = debouncedManagerSearch.trim().toLowerCase();

    return products
      .map((product) => ({
        ...product,
        access: productAccessAtAddress(managerAddress, product.id, perItemPricing),
      }))
      .filter((product) => {
        if (!query) {
          return true;
        }

        return [product.name, product.description]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));
      });
  }, [products, managerAddress, perItemPricing, debouncedManagerSearch]);

  const productManagerGroups = useMemo(
    () => splitProductsByLocationAccess(managerProducts),
    [managerProducts]
  );

  const activeOrganizationLoadOptions = useMemo(
    () =>
      (Array.isArray(organizationLoadOptions) ? organizationLoadOptions : [])
        .filter((option: any) => option.active !== false && !option.deletedAt)
        .sort((a: any, b: any) => String(a.name ?? '').localeCompare(String(b.name ?? ''))),
    [organizationLoadOptions]
  );

  const managerLocationLoadOptions = useMemo(
    () => activeLocationLoadOptions(managerAddress),
    [managerAddress]
  );

  const managerExplicitlyEnabledLoadOptionIds = useMemo(
    () => new Set<string>(managerLocationLoadOptions.map((option: any) => option.id)),
    [managerLocationLoadOptions]
  );

  const managerHasLocationSpecificLoadOptions = managerLocationLoadOptions.length > 0;

  const managerEffectiveFulfillmentDefaultId = managerHasLocationSpecificLoadOptions
    ? addressDefaultLoadOptionId(managerAddress) || null
    : (organizationDefaultLoadOption?.id ?? null);

  const compatibleAddressesForProduct = (productId: string) => {
    if (!productId || !perItemPricing) {
      return addresses;
    }

    const enabledAddressIds = enabledAddressIdsByProductId.get(productId);

    if (!enabledAddressIds?.size) {
      return [];
    }

    return addresses.filter((address: any) => enabledAddressIds.has(address.id));
  };

  const productWarehouseIds = (product: LocationAwareProduct | null): string[] => {
    if (!product) {
      return [];
    }

    const ids = (product.stocks ?? [])

      .map((stock: any) => stock?.locationId)

      .filter(Boolean)

      .map(String);

    return Array.from(new Set(ids));
  };

  const warehousesForProduct = (product: LocationAwareProduct | null) => {
    const ids = productWarehouseIds(product);

    return ids.map((id) => {
      const location = allLocations.find((candidate: any) => candidate.id === id);

      return (
        location ?? {
          id,

          name: id,
        }
      );
    });
  };

  const loadOptionsForAddress = (deliveryAddressId: string) => {
    const address = addresses.find((candidate: any) => candidate.id === deliveryAddressId) ?? null;

    const locationLoadOptions = activeLocationLoadOptions(address);

    return buildLoadOptions(locationLoadOptions, organizationDefaultLoadOption);
  };

  const defaultLoadOptionForAddress = (deliveryAddressId: string) => {
    const address = addresses.find((candidate: any) => candidate.id === deliveryAddressId) ?? null;

    const locationLoadOptions = activeLocationLoadOptions(address);

    return effectiveDefaultLoadOptionId(
      locationLoadOptions,

      addressDefaultLoadOptionId(address),

      organizationDefaultLoadOption
    );
  };

  const preferredAddressId = (productId: string, currentAddressId = '') => {
    const compatible = compatibleAddressesForProduct(productId);

    if (currentAddressId && compatible.some((address: any) => address.id === currentAddressId)) {
      return currentAddressId;
    }

    const primary = compatible.find((address: any) => address.isPrimary === true);

    if (primary) {
      return primary.id;
    }

    const headquarters = compatible.find((address: any) => address.isHeadquarters === true);

    if (headquarters) {
      return headquarters.id;
    }

    if (compatible.length === 1) {
      return compatible[0].id;
    }

    return '';
  };

  const preferredWarehouseId = (product: LocationAwareProduct | null, currentWarehouseId = '') => {
    const warehouseIds = productWarehouseIds(product);

    if (currentWarehouseId && warehouseIds.includes(currentWarehouseId)) {
      return currentWarehouseId;
    }

    if (product?.defaultLocationId && warehouseIds.includes(product.defaultLocationId)) {
      return product.defaultLocationId;
    }

    if (warehouseIds.length === 1) {
      return warehouseIds[0];
    }

    return '';
  };

  const preferredLoadOptionId = (deliveryAddressId: string, currentLoadOptionId = '') => {
    if (!deliveryAddressId) {
      return '';
    }

    const options = loadOptionsForAddress(deliveryAddressId);

    if (currentLoadOptionId && options.some((option: any) => option.id === currentLoadOptionId)) {
      return currentLoadOptionId;
    }

    const defaultId = defaultLoadOptionForAddress(deliveryAddressId);

    if (defaultId && options.some((option: any) => option.id === defaultId)) {
      return defaultId;
    }

    if (options.length === 1) {
      return options[0].id;
    }

    return '';
  };

  const preferredPalletSizeId = (
    product: LocationAwareProduct | null,

    currentPalletSizeId = ''
  ) => {
    if (!product || product.deliveryType !== 'PALLET') {
      return '';
    }

    const palletSizes = Array.isArray(product.palletSizes) ? product.palletSizes : [];

    if (
      currentPalletSizeId &&
      palletSizes.some((pallet: any) => pallet.id === currentPalletSizeId)
    ) {
      return currentPalletSizeId;
    }

    if (palletSizes.length === 1) {
      return palletSizes[0].id;
    }

    return '';
  };

  const selectedLoadOptionForRow = (row: BatchRowState) =>
    loadOptionsForAddress(row.deliveryAddressId).find(
      (option: any) => option.id === row.loadOptionId
    ) ?? null;

  const carrierRateEnabledForRow = (row: BatchRowState) =>
    selectedLoadOptionForRow(row)?.enableCarrierRate !== false;

  const configuredCarrierRateForRow = (row: BatchRowState) => {
    const address = addressForRow(row);

    if (!address || !row.warehouseId) {
      return null;
    }

    const rates = Array.isArray(address.carrierRates) ? address.carrierRates : [];

    return rates.find((rate: any) => rate.warehouseId === row.warehouseId) ?? null;
  };

  const carrierRateValueForRow = (row: BatchRowState) => {
    if (row.carrierRateTouched) {
      return row.carrierRateInput;
    }

    if (!row.deliveryAddressId || !row.warehouseId || !row.loadOptionId) {
      return '';
    }

    if (!carrierRateEnabledForRow(row)) {
      return '';
    }

    const configured = configuredCarrierRateForRow(row);

    return centsToDollarsInput(configured?.carrierRateCents ?? 0);
  };

  const reconcileRow = (
    row: BatchRowState,

    changed: {
      product?: boolean;

      address?: boolean;

      warehouse?: boolean;

      loadOption?: boolean;
    } = {}
  ): BatchRowState => {
    let next = {
      ...row,

      error: null,
    };

    const product = productForRow(next);

    if (changed.product) {
      next.deliveryAddressId = preferredAddressId(next.productId, next.deliveryAddressId);

      /*

       * Product change must resolve warehouse from the NEW product itself.

       *

       * Do not preserve the warehouse copied from the previous row, even when

       * that warehouse also happens to stock the newly selected product.

       *

       * Resolution remains:

       * 1. product.defaultLocationId, when valid

       * 2. the only product warehouse, when there is exactly one

       * 3. otherwise blank so the admin explicitly chooses

       */

      next.warehouseId = preferredWarehouseId(product, '');

      next.palletSizeId = preferredPalletSizeId(product, next.palletSizeId);

      next.loadOptionId = preferredLoadOptionId(next.deliveryAddressId, next.loadOptionId);

      next.carrierRateTouched = false;

      next.carrierRateInput = '';
    }

    if (changed.address) {
      next.loadOptionId = preferredLoadOptionId(next.deliveryAddressId, next.loadOptionId);

      next.carrierRateTouched = false;

      next.carrierRateInput = '';
    }

    if (changed.warehouse || changed.loadOption) {
      next.carrierRateTouched = false;

      next.carrierRateInput = '';
    }

    /*

     * Qty (t) follows the selected fulfilment method's capacityLabel.

     *

     * - Changing Product can auto-resolve a fulfilment method.

     * - Changing Ship To can auto-resolve a fulfilment method.

     * - Changing Fulfilment Method directly must immediately replace Qty.

     *

     * If the selected method has no valid capacityLabel, clear Qty so the row

     * cannot silently keep the tonnage from the previously selected method.

     *

     * We still seed a blank Qty from the current method during other row

     * reconciliation, but warehouse-only changes do not overwrite Qty.

     */

    const selectedOption = selectedLoadOptionForRow(next);

    const methodCapacity = Number(selectedOption?.capacityLabel);

    const hasValidMethodCapacity = Number.isFinite(methodCapacity) && methodCapacity > 0;

    if (changed.product || changed.address || changed.loadOption) {
      next.quantity = hasValidMethodCapacity ? String(methodCapacity) : '';
    } else if (!next.quantity.trim() && hasValidMethodCapacity) {
      next.quantity = String(methodCapacity);
    }

    return next;
  };

  const patchRow = (
    key: string,

    patch: Partial<BatchRowState>,

    changed: {
      product?: boolean;

      address?: boolean;

      warehouse?: boolean;

      loadOption?: boolean;
    } = {}
  ) => {
    if (changed.address && manager?.rowKey === key) {
      setManager(null);
      setManagerSearch('');
    }

    setRows((current) =>
      current.map((row) => {
        if (row.key !== key) {
          return row;
        }

        return reconcileRow(
          {
            ...row,

            ...patch,
          },

          changed
        );
      })
    );
  };

  const selectManagedProductForRow = (
    rowKey: string,
    product: LocationAwareProduct,
    deliveryAddressId: string
  ) => {
    setRows((current) =>
      current.map((row) => {
        if (row.key !== rowKey) {
          return row;
        }

        /*
         * Keep the manager's delivery location. We deliberately reconcile as an
         * address change rather than a product change here because a product that
         * was just enabled is not necessarily present in the previous render's
         * enabled-address map yet.
         */
        return reconcileRow(
          {
            ...row,
            productId: product.id,
            deliveryAddressId,
            warehouseId: preferredWarehouseId(product, ''),
            palletSizeId: preferredPalletSizeId(product, ''),
            carrierRateTouched: false,
            carrierRateInput: '',
            error: null,
          },
          {
            address: true,
          }
        );
      })
    );
  };

  const handlePickManagedProduct = async (
    productId: string,
    access: ProductLocationAccessState
  ) => {
    if (!managerRow || manager?.kind !== 'products' || enablingProduct) {
      return;
    }

    if (!managerRow.deliveryAddressId) {
      toast.warn('Select a delivery location before managing product access.');
      return;
    }

    const picked = products.find((product) => product.id === productId);

    if (!picked) {
      toast.error('Product could not be found.');
      return;
    }

    /*
     * If access is already enabled (or per-item pricing is off), selecting the
     * product requires no access mutation.
     */
    if (!perItemPricing || access === 'ENABLED') {
      selectManagedProductForRow(managerRow.key, picked, managerRow.deliveryAddressId);
      return;
    }

    setEnablingProduct(true);

    try {
      await setLocationProductAccess.mutateAsync({
        addressId: managerRow.deliveryAddressId,
        productId,
        isEnabled: true,
        isPreview: false,
      });

      await refetchAddresses();

      selectManagedProductForRow(managerRow.key, picked, managerRow.deliveryAddressId);

      toast.success('Product enabled for this delivery location.');
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error || 'Could not enable that product for this delivery location.'
      );
    } finally {
      setEnablingProduct(false);
    }
  };

  const handleEnableManagedMethod = async (methodId: string) => {
    if (!managerRow || manager?.kind !== 'methods' || !managerRow.deliveryAddressId) {
      return;
    }

    try {
      await enableLocationLoadOption.mutateAsync({
        addressId: managerRow.deliveryAddressId,
        loadOptionId: methodId,
      });

      await refetchAddresses();

      const method = organizationLoadOptions.find((option: any) => option.id === methodId);

      const capacity = Number(method?.capacityLabel);
      const quantity = Number.isFinite(capacity) && capacity > 0 ? String(capacity) : '';

      setRows((current) =>
        current.map((row) =>
          row.key === managerRow.key
            ? {
                ...row,
                loadOptionId: methodId,
                quantity,
                carrierRateTouched: false,
                carrierRateInput: '',
                error: null,
              }
            : row
        )
      );

      toast.success('Fulfilment method enabled for this delivery location.');
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ||
          'Failed to enable fulfilment method for this delivery location.'
      );
    }
  };

  const handleDisableManagedMethod = async (methodId: string) => {
    if (!managerRow || manager?.kind !== 'methods' || !managerRow.deliveryAddressId) {
      return;
    }

    try {
      await disableLocationLoadOption.mutateAsync({
        addressId: managerRow.deliveryAddressId,
        loadOptionId: methodId,
      });

      await refetchAddresses();

      if (managerRow.loadOptionId === methodId) {
        setRows((current) =>
          current.map((row) =>
            row.key === managerRow.key
              ? {
                  ...row,
                  loadOptionId: '',
                  quantity: '',
                  carrierRateTouched: false,
                  carrierRateInput: '',
                  error: null,
                }
              : row
          )
        );
      }

      toast.success('Fulfilment method disabled for this delivery location.');
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ||
          'Failed to disable fulfilment method for this delivery location.'
      );
    }
  };

  const toggleRowManager = (kind: 'products' | 'methods', row: BatchRowState) => {
    let deliveryAddressId = row.deliveryAddressId;

    /*
     * Managing products/methods must NOT depend on having a product selected.
     *
     * Both managers are scoped to a delivery location. If Ship To is blank,
     * resolve an obvious location first:
     * 1. Primary
     * 2. Headquarters
     * 3. Only available location
     *
     * If several locations remain ambiguous, keep the action enabled and ask
     * the admin to choose Ship To first.
     */
    if (!deliveryAddressId) {
      const candidates = row.productId ? compatibleAddressesForProduct(row.productId) : addresses;

      const preferredAddress =
        candidates.find((address: any) => address.isPrimary === true) ??
        candidates.find((address: any) => address.isHeadquarters === true) ??
        (candidates.length === 1 ? candidates[0] : null);

      if (!preferredAddress) {
        toast.warn('Select a Ship To location first.');
        return;
      }

      deliveryAddressId = preferredAddress.id;

      setRows((current) =>
        current.map((candidate) =>
          candidate.key === row.key
            ? reconcileRow(
                {
                  ...candidate,
                  deliveryAddressId,
                },
                {
                  address: true,
                }
              )
            : candidate
        )
      );
    }

    setManagerSearch('');

    setManager((current) =>
      current?.kind === kind && current.rowKey === row.key
        ? null
        : {
            kind,
            rowKey: row.key,
          }
    );
  };

  const addRow = () => {
    setRows((current) => {
      if (current.length >= MAX_ORDERS) {
        return current;
      }

      const last = current[current.length - 1];

      if (!last) {
        return [makeEmptyRow()];
      }

      return [
        ...current,

        {
          ...last,

          key: nextRowKey(),

          poNumber: '',

          note: '',

          error: null,
        },
      ];
    });
  };

  const setCopies = (count: number) => {
    const target = Math.max(1, Math.min(MAX_ORDERS, count));

    setRows((current) => {
      if (target === current.length) {
        return current;
      }

      if (target < current.length) {
        return current.slice(0, target);
      }

      const last = current[current.length - 1] ?? makeEmptyRow();

      const additions = Array.from({ length: target - current.length }, () => ({
        ...last,

        key: nextRowKey(),

        poNumber: '',

        note: '',

        error: null,
      }));

      return [...current, ...additions];
    });
  };

  const removeRow = (key: string) => {
    if (manager?.rowKey === key) {
      setManager(null);
      setManagerSearch('');
    }

    setRows((current) =>
      current.length <= 1 ? current : current.filter((row) => row.key !== key)
    );
  };

  const validateCarrierRateForRow = (row: BatchRowState): string | null => {
    if (!row.carrierRateTouched) {
      return null;
    }

    if (!carrierRateEnabledForRow(row)) {
      return 'Carrier rates are disabled for the selected fulfilment method.';
    }

    const normalized = row.carrierRateInput.trim();

    if (!normalized) {
      return 'Carrier rate is required after editing it.';
    }

    const value = Number(normalized);

    if (!Number.isFinite(value) || value < 0) {
      return 'Carrier rate must be a valid non-negative amount.';
    }

    const decimals = normalized.split('.')[1]?.length ?? 0;

    if (decimals > 2) {
      return 'Carrier rate cannot have more than two decimal places.';
    }

    return null;
  };

  const validationErrorForRow = (row: BatchRowState): string | null => {
    if (!resolvedCustomerId) {
      return 'Customer is required.';
    }

    const product = productForRow(row);

    if (!product) {
      return 'Product is required.';
    }

    const quantity = Number(row.quantity);

    if (!Number.isFinite(quantity) || quantity <= 0) {
      return 'Quantity must be greater than 0.';
    }

    const compatibleAddresses = compatibleAddressesForProduct(row.productId);

    if (
      !row.deliveryAddressId ||
      !compatibleAddresses.some((address: any) => address.id === row.deliveryAddressId)
    ) {
      return 'Select a delivery location where this product is enabled.';
    }

    const address = addressForRow(row);

    if (productAccessAtAddress(address, row.productId, perItemPricing) !== 'ENABLED') {
      return 'Product is not enabled for this delivery location.';
    }

    const warehouseIds = productWarehouseIds(product);

    if (!row.warehouseId || !warehouseIds.includes(row.warehouseId)) {
      return 'Select a valid fulfilment warehouse.';
    }

    const options = loadOptionsForAddress(row.deliveryAddressId);

    if (!row.loadOptionId || !options.some((option: any) => option.id === row.loadOptionId)) {
      return 'Select a valid fulfilment method.';
    }

    if (product.deliveryType === 'PALLET') {
      const palletSizes = Array.isArray(product.palletSizes) ? product.palletSizes : [];

      if (!row.palletSizeId || !palletSizes.some((pallet: any) => pallet.id === row.palletSizeId)) {
        return 'Select a pallet size.';
      }
    }

    if (address?.requiresPoNumber === true && !row.poNumber.trim()) {
      return 'PO number is required for this delivery location.';
    }

    return validateCarrierRateForRow(row);
  };

  const canCreate =
    Boolean(resolvedCustomerId) &&
    rows.length > 0 &&
    rows.every((row) => validationErrorForRow(row) === null) &&
    !isSubmitting &&
    !addressesLoading &&
    !productsQuery.isLoading &&
    !locationsLoading &&
    !loadOptionsLoading;

  const buildPayloadForRow = (row: BatchRowState) => {
    const product = productForRow(row);

    const isPallet = product?.deliveryType === 'PALLET';

    const basePayload = buildAdminOrderPayload({
      customerId: resolvedCustomerId,

      productId: row.productId,

      loadOptionId: row.loadOptionId,

      isPallet,

      palletSizeId: row.palletSizeId,

      deliveryAddressId: row.deliveryAddressId,

      note: row.note,

      poNumber: row.poNumber,
    });

    return {
      ...basePayload,

      warehouseId: row.warehouseId,

      /*

       * Explicit admin quantity.

       *

       * The checkout patch included with this update makes an explicit quantity

       * win over the fulfilment method capacity while preserving the old fallback

       * when quantity is omitted.

       */

      quantity: Number(row.quantity),

      ...(row.carrierRateTouched && carrierRateEnabledForRow(row)
        ? {
            carrierRateOverrideCents: dollarsInputToCents(row.carrierRateInput),
          }
        : {}),
    };
  };

  const submitAll = async () => {
    if (!canCreate) {
      return;
    }

    setIsSubmitting(true);

    let createdCount = 0;

    const failedRows: BatchRowState[] = [];

    /*

     * Intentionally sequential.

     *

     * Checkout allocates load numbers under a serializable transaction. Sending

     * many orders for the same organization concurrently only increases contention

     * on that organization-level load-number counter.

     */

    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];

      try {
        await order.mutateAsync(buildPayloadForRow(row));

        createdCount += 1;
      } catch (error: any) {
        failedRows.push({
          ...row,

          error: apiErrorMessage(error, `Order ${index + 1} failed.`),
        });
      }
    }

    setIsSubmitting(false);

    if (failedRows.length === 0) {
      toast.success(
        `${createdCount} order${createdCount === 1 ? '' : 's'} submitted successfully.`
      );

      onClose();

      return;
    }

    setRows(failedRows);

    if (createdCount > 0) {
      toast.warn(
        `${createdCount} order${createdCount === 1 ? '' : 's'} created; ` +
          `${failedRows.length} failed. Failed rows remain below so you can correct and retry them.`
      );
    } else {
      toast.error(
        `No orders were created. ${failedRows.length} row${failedRows.length === 1 ? '' : 's'} need attention.`
      );
    }
  };

  const resetForCustomer = () => {
    setManager(null);
    setManagerSearch('');
    setRows([makeEmptyRow()]);
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    setCustomerSearch('');

    setSelectedCustomerId(customerId ?? '');

    setSelectedCustomer(null);

    setShowCustomerList(false);
    setManager(null);
    setManagerSearch('');
    setRows([makeEmptyRow()]);
    setIsSubmitting(false);
  }, [open, customerId]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowCustomerList(false);
        setManager(null);
        setManagerSearch('');
      }
    };

    const onDocMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (customerComboRef.current && !customerComboRef.current.contains(target)) {
        setShowCustomerList(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);

    document.addEventListener('mousedown', onDocMouseDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);

      document.removeEventListener('mousedown', onDocMouseDown);
    };
  }, [open]);

  if (!open) {
    return null;
  }

  const displayCustomerName =
    customerLabel ||
    formatCustomerLabel(effectiveSelectedCustomer) ||
    resolvedCustomerId ||
    'Select customer';

  return (
    <div className="modal admin-multi-order-modal" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={isSubmitting ? undefined : onClose} />

      <div className="modal__panel admin-multi-order-modal__panel">
        <div className="modal__header">
          <div>
            <h3 className="modal__title">Place Orders</h3>

            <div className="muted tiny" style={{ marginTop: 4 }}>
              For: <strong>{displayCustomerName}</strong>. Each row creates one new order/load.
            </div>
          </div>

          <button className="btn btn--ghost" onClick={onClose} disabled={isSubmitting}>
            Close
          </button>
        </div>

        <div className="modal__body">
          {!customerId && (
            <div className="card admin-multi-order-modal__customer-card">
              <div className="form-control" style={{ marginBottom: 0 }}>
                <label>Customer</label>

                <div ref={customerComboRef} style={{ position: 'relative' }}>
                  <SearchInput
                    className="select-lg"
                    value={customerSearch}
                    disabled={isSubmitting}
                    onValueChange={(value) => {
                      setCustomerSearch(value);

                      setSelectedCustomerId('');

                      setSelectedCustomer(null);

                      setShowCustomerList(true);

                      resetForCustomer();
                    }}
                    onFocus={() => setShowCustomerList(true)}
                    placeholder="Search customers…"
                  />

                  {showCustomerList && (
                    <div className="admin-multi-order-modal__customer-results">
                      {customersQuery.isLoading ? (
                        <div className="muted admin-multi-order-modal__customer-result">
                          Searching…
                        </div>
                      ) : customers.length === 0 ? (
                        <div className="muted admin-multi-order-modal__customer-result">
                          No customers match "{customerSearch}".
                        </div>
                      ) : (
                        customers.map((customer: any) => (
                          <button
                            key={customer.id}
                            type="button"
                            className="btn btn--ghost admin-multi-order-modal__customer-result-button"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => {
                              setSelectedCustomerId(customer.id);

                              setSelectedCustomer(customer);

                              setCustomerSearch(formatCustomerLabel(customer) || '');

                              setShowCustomerList(false);

                              resetForCustomer();
                            }}
                          >
                            <strong>{formatCustomerLabel(customer)}</strong>

                            {customer.email && <span className="muted tiny">{customer.email}</span>}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="admin-multi-order-modal__controls">
            <div className="admin-multi-order-modal__copies">
              <span className="admin-multi-order-modal__copies-label">Orders</span>

              <div className="admin-multi-order-modal__stepper">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setCopies(rows.length - 1)}
                  disabled={rows.length <= 1 || isSubmitting}
                  aria-label="One fewer order"
                >
                  −
                </button>

                <input
                  type="number"
                  min={1}
                  max={MAX_ORDERS}
                  value={rows.length}
                  disabled={isSubmitting}
                  onChange={(event) => setCopies(Number(event.target.value) || 1)}
                  className="admin-multi-order-modal__copies-input"
                />

                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setCopies(rows.length + 1)}
                  disabled={rows.length >= MAX_ORDERS || isSubmitting}
                  aria-label="One more order"
                >
                  +
                </button>
              </div>
            </div>

            <span className="muted tiny">
              Configure up to {MAX_ORDERS} orders, then submit them together.
            </span>
          </div>

          {!resolvedCustomerId ? (
            <div className="card admin-multi-order-modal__empty">
              Select a customer to configure orders.
            </div>
          ) : addressesLoading ||
            productsQuery.isLoading ||
            locationsLoading ||
            loadOptionsLoading ? (
            <div className="skeleton" style={{ height: 220 }} />
          ) : (
            <div className="admin-multi-order-modal__table-wrap">
              <table className="table admin-multi-order-modal__table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>

                    <th>Product</th>

                    <th style={{ width: 100 }}>Qty (t)</th>

                    <th style={{ width: 140 }}>PO</th>

                    <th>Ship To</th>

                    <th>Warehouse</th>

                    <th>Fulfilment Method</th>

                    <th style={{ width: 150 }}>Pallet</th>

                    <th style={{ width: 145 }}>Carrier Rate</th>

                    <th>Pickup Note</th>

                    <th style={{ width: 44 }} />
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row, index) => {
                    const product = productForRow(row);

                    const deliveryAddresses = compatibleAddressesForProduct(row.productId);

                    const selectedAddress = addressForRow(row);

                    const warehouses = warehousesForProduct(product);

                    const methods = loadOptionsForAddress(row.deliveryAddressId);

                    const palletSizes =
                      product?.deliveryType === 'PALLET' && Array.isArray(product.palletSizes)
                        ? product.palletSizes
                        : [];

                    const requiresPo = selectedAddress?.requiresPoNumber === true;

                    const configuredCarrierRate = configuredCarrierRateForRow(row);

                    const carrierRateEnabled = carrierRateEnabledForRow(row);

                    const rowValidationError = validationErrorForRow(row);

                    const visibleError = row.error || rowValidationError;

                    return (
                      <tr
                        key={row.key}
                        className={row.error ? 'admin-multi-order-modal__row--error' : ''}
                      >
                        <td className="muted">{index + 1}</td>

                        <td>
                          <SearchableSelect
                            value={row.productId}
                            options={productOptions}
                            onChange={(id) =>
                              patchRow(
                                row.key,
                                {
                                  productId: id,
                                  palletSizeId: '',
                                },
                                {
                                  product: true,
                                }
                              )
                            }
                            placeholder="Search products…"
                            ariaLabel={`Product for order ${index + 1}`}
                          />

                          <button
                            type="button"
                            className="admin-multi-order-modal__cell-link"
                            onClick={() => toggleRowManager('products', row)}
                            disabled={
                              !resolvedCustomerId ||
                              addressesLoading ||
                              addresses.length === 0 ||
                              enablingProduct ||
                              isSubmitting
                            }
                            title={
                              row.deliveryAddressId
                                ? 'Manage products for this delivery location'
                                : 'Manage products after choosing, or automatically resolving, Ship To'
                            }
                          >
                            {manager?.kind === 'products' && manager.rowKey === row.key
                              ? 'Done'
                              : 'Manage products'}
                          </button>
                        </td>

                        <td>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={row.quantity}
                            disabled={!row.productId || isSubmitting}
                            onChange={(event) =>
                              patchRow(row.key, {
                                quantity: event.target.value,

                                error: null,
                              })
                            }
                            placeholder="Tons"
                          />
                        </td>

                        <td>
                          <input
                            type="text"
                            value={row.poNumber}
                            disabled={!row.deliveryAddressId || isSubmitting}
                            placeholder={requiresPo ? 'Required' : 'Optional'}
                            className={
                              requiresPo && !row.poNumber.trim()
                                ? 'admin-multi-order-modal__required-input'
                                : ''
                            }
                            onChange={(event) =>
                              patchRow(row.key, {
                                poNumber: event.target.value,

                                error: null,
                              })
                            }
                          />

                          {requiresPo && !row.poNumber.trim() && (
                            <div className="admin-multi-order-modal__hint--error">PO required</div>
                          )}
                        </td>

                        <td>
                          <select
                            value={row.deliveryAddressId}
                            disabled={!resolvedCustomerId || addresses.length === 0 || isSubmitting}
                            onChange={(event) =>
                              patchRow(
                                row.key,

                                {
                                  deliveryAddressId: event.target.value,
                                },

                                {
                                  address: true,
                                }
                              )
                            }
                          >
                            <option value="">
                              {!resolvedCustomerId
                                ? 'Select customer first'
                                : !addresses.length
                                  ? 'No locations'
                                  : !row.productId
                                    ? 'Select location…'
                                    : deliveryAddresses.length
                                      ? 'Select enabled location…'
                                      : 'No enabled locations'}
                            </option>

                            {deliveryAddresses.map((address: any) => (
                              <option key={address.id} value={address.id}>
                                {addressLabel(address)}

                                {address.isPrimary ? ' • Primary' : ''}

                                {address.isHeadquarters ? ' • HQ' : ''}

                                {address.onFreeze ? ' • Frozen' : ''}

                                {address.requiresPoNumber ? ' • PO' : ''}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td>
                          <select
                            value={row.warehouseId}
                            disabled={!row.productId || warehouses.length === 0 || isSubmitting}
                            onChange={(event) =>
                              patchRow(
                                row.key,

                                {
                                  warehouseId: event.target.value,
                                },

                                {
                                  warehouse: true,
                                }
                              )
                            }
                          >
                            <option value="">
                              {!row.productId
                                ? 'Select product first'
                                : warehouses.length
                                  ? 'Select warehouse…'
                                  : 'No warehouse'}
                            </option>

                            {warehouses.map((warehouse: any) => (
                              <option key={warehouse.id} value={warehouse.id}>
                                {warehouse.name || warehouse.label || warehouse.id}

                                {warehouse.city ? ` • ${warehouse.city}` : ''}

                                {warehouse.state ? `, ${warehouse.state}` : ''}

                                {warehouse.id === product?.defaultLocationId ? ' • Default' : ''}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td>
                          <select
                            value={row.loadOptionId}
                            disabled={
                              !row.deliveryAddressId || methods.length === 0 || isSubmitting
                            }
                            onChange={(event) =>
                              patchRow(
                                row.key,

                                {
                                  loadOptionId: event.target.value,
                                },

                                {
                                  loadOption: true,
                                }
                              )
                            }
                          >
                            <option value="">
                              {!row.deliveryAddressId
                                ? 'Select location first'
                                : methods.length
                                  ? 'Select method…'
                                  : 'No methods'}
                            </option>

                            {methods.map((method: any) => (
                              <option key={method.id} value={method.id}>
                                {method.name}

                                {method.capacityLabel ? ` • ${method.capacityLabel}t` : ''}

                                {method.id === defaultLoadOptionForAddress(row.deliveryAddressId)
                                  ? ' • Default'
                                  : ''}
                              </option>
                            ))}
                          </select>

                          <button
                            type="button"
                            className="admin-multi-order-modal__cell-link"
                            onClick={() => toggleRowManager('methods', row)}
                            disabled={
                              !resolvedCustomerId ||
                              addressesLoading ||
                              addresses.length === 0 ||
                              loadOptionsLoading ||
                              methodChangePending ||
                              isSubmitting
                            }
                            title={
                              row.deliveryAddressId
                                ? 'Manage fulfilment methods for this delivery location'
                                : 'Manage methods after choosing, or automatically resolving, Ship To'
                            }
                          >
                            {manager?.kind === 'methods' && manager.rowKey === row.key
                              ? 'Done'
                              : 'Manage methods'}
                          </button>
                        </td>

                        <td>
                          {product?.deliveryType === 'PALLET' ? (
                            <select
                              value={row.palletSizeId}
                              disabled={palletSizes.length === 0 || isSubmitting}
                              onChange={(event) =>
                                patchRow(row.key, {
                                  palletSizeId: event.target.value,

                                  error: null,
                                })
                              }
                            >
                              <option value="">
                                {palletSizes.length ? 'Select pallet…' : 'No pallets'}
                              </option>

                              {palletSizes.map((pallet: any) => (
                                <option key={pallet.id} value={pallet.id}>
                                  {pallet?.template?.name || 'Pallet'}

                                  {pallet?.template?.weightTons
                                    ? ` • ${pallet.template.weightTons}t`
                                    : ''}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>

                        <td>
                          <div className="admin-multi-order-modal__money-input">
                            <span>$</span>

                            <input
                              type="text"
                              inputMode="decimal"
                              value={carrierRateValueForRow(row)}
                              disabled={
                                !row.deliveryAddressId ||
                                !row.warehouseId ||
                                !row.loadOptionId ||
                                !carrierRateEnabled ||
                                isSubmitting
                              }
                              onChange={(event) =>
                                patchRow(row.key, {
                                  carrierRateInput: limitMoneyInput(event.target.value),

                                  carrierRateTouched: true,

                                  error: null,
                                })
                              }
                              placeholder="0.00"
                            />
                          </div>

                          {row.deliveryAddressId &&
                            row.warehouseId &&
                            row.loadOptionId &&
                            carrierRateEnabled &&
                            configuredCarrierRate === null &&
                            !row.carrierRateTouched && (
                              <div className="admin-multi-order-modal__hint--warn">
                                No carrier rate configured — this order will use $0.00
                              </div>
                            )}

                          {row.carrierRateTouched && <div className="muted tiny">Override</div>}
                        </td>

                        <td>
                          <input
                            type="text"
                            value={row.note}
                            disabled={isSubmitting}
                            onChange={(event) =>
                              patchRow(row.key, {
                                note: event.target.value,

                                error: null,
                              })
                            }
                            placeholder="Optional"
                          />

                          {visibleError && (
                            <div
                              className={
                                row.error
                                  ? 'admin-multi-order-modal__row-error'
                                  : 'admin-multi-order-modal__hint--error'
                              }
                            >
                              {visibleError}
                            </div>
                          )}
                        </td>

                        <td>
                          <button
                            type="button"
                            className="btn btn--ghost admin-multi-order-modal__remove"
                            onClick={() => removeRow(row.key)}
                            disabled={rows.length <= 1 || isSubmitting}
                            aria-label={`Remove order ${index + 1}`}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {manager && managerRow && managerAddress && (
                <div className="admin-multi-order-modal__manager">
                  <div className="admin-multi-order-modal__manager-header">
                    <div>
                      <strong>
                        Order {rows.findIndex((row) => row.key === managerRow.key) + 1}
                        {' · '}
                        {manager.kind === 'products'
                          ? 'Manage products'
                          : 'Manage fulfilment methods'}
                      </strong>

                      <div className="muted tiny" style={{ marginTop: 3 }}>
                        {productLocationLabel(managerAddress)}
                      </div>
                    </div>

                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => {
                        setManager(null);
                        setManagerSearch('');
                      }}
                    >
                      Done
                    </button>
                  </div>

                  {manager.kind === 'products' ? (
                    <ProductAccessManager
                      groups={productManagerGroups}
                      selectedProductId={managerRow.productId}
                      onPick={handlePickManagedProduct}
                      locationLabel={productLocationLabel(managerAddress)}
                      search={managerSearch}
                      onSearchChange={setManagerSearch}
                      hasActiveSearch={Boolean(debouncedManagerSearch)}
                      fetching={productsQuery.isLoading || productsQuery.isFetching}
                      disabled={enablingProduct}
                    />
                  ) : (
                    <LocationLoadOptionManager
                      options={activeOrganizationLoadOptions}
                      explicitlyEnabledIds={managerExplicitlyEnabledLoadOptionIds}
                      hasLocationSpecificLoadOptions={managerHasLocationSpecificLoadOptions}
                      defaultFulfillmentMethodId={managerEffectiveFulfillmentDefaultId}
                      onEnable={handleEnableManagedMethod}
                      onDisable={handleDisableManagedMethod}
                      pending={methodChangePending}
                    />
                  )}
                </div>
              )}

              <button
                type="button"
                className="btn btn--ghost admin-multi-order-modal__add"
                onClick={addRow}
                disabled={rows.length >= MAX_ORDERS || isSubmitting}
              >
                + Add another order
              </button>
            </div>
          )}
        </div>

        <div className="modal__footer admin-multi-order-modal__footer">
          <span className="muted tiny">
            {rows.length} order{rows.length === 1 ? '' : 's'} will be submitted sequentially.
            {rows.some((row) => row.error) && ' Failed rows can be corrected and retried.'}
          </span>

          <div className="actions" style={{ gap: 8 }}>
            <button className="btn btn--ghost" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>

            <button className="btn btn--primary" onClick={submitAll} disabled={!canCreate}>
              {isSubmitting
                ? 'Submitting…'
                : `Place ${rows.length} Order${rows.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
